// src/pages/Schedules.js
// Scheduled SCRUTINIZE Assessments Management Page
// Terminal Theme - Purple Accent (--neon-purple: #B026FF)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import { track as trackTelemetry } from '../services/telemetryService';
import './Schedules.css';

// Industry options for AI-customized analysis
const INDUSTRIES = [
  { code: 'healthcare', label: 'Healthcare & Life Sciences' },
  { code: 'financial', label: 'Financial Services & Banking' },
  { code: 'government', label: 'Government & Public Sector' },
  { code: 'technology', label: 'Technology & Software' },
  { code: 'retail', label: 'Retail & E-Commerce' },
  { code: 'manufacturing', label: 'Manufacturing & Industrial' },
  { code: 'education', label: 'Education & Research' },
  { code: 'energy', label: 'Energy & Utilities' },
  { code: 'telecom', label: 'Telecommunications' },
  { code: 'media', label: 'Media & Entertainment' },
  { code: 'legal', label: 'Legal & Professional Services' },
  { code: 'hospitality', label: 'Hospitality & Travel' },
  { code: 'nonprofit', label: 'Non-Profit & NGO' },
  { code: 'defense', label: 'Defense & Aerospace' },
  { code: 'realestate', label: 'Real Estate & Construction' },
  { code: 'logistics', label: 'Logistics & Transportation' },
  { code: 'agriculture', label: 'Agriculture & Food' },
  { code: 'insurance', label: 'Insurance' },
  { code: 'pharma', label: 'Pharmaceutical' },
  { code: 'automotive', label: 'Automotive' }
];

// Tier limits configuration — matches tierConfig.js (single source of truth)
const TIER_LIMITS = {
  trial:      { schedules: 3,    scrutinizePerMonth: 20 },
  free:       { schedules: 0,    scrutinizePerMonth: 0 },
  standard:   { schedules: 3,    scrutinizePerMonth: 20 },
  pro:        { schedules: 15,   scrutinizePerMonth: 100 },
  enterprise: { schedules: null, scrutinizePerMonth: null }
};

// Day names for weekly schedule display
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function Schedules() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  // State
  const [schedules, setSchedules] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sidebar state
  const [activeSectionOpen, setActiveSectionOpen] = useState(true);
  const [pausedSectionOpen, setPausedSectionOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    target: '',
    industry: '',
    frequency: 'weekly',
    weeklyDays: [],
    monthlyDates: [],
    hour: 9,
    minute: 0,
    recipients: '',
    reportFormat: 'link'
  });
  
  // Detect user timezone on mount
  const [userTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  
  // API base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';
  
  // Get auth token
  const getToken = useCallback(async () => {
    if (!accounts[0]) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: accounts[0]
      });
      return response.idToken;
    } catch (err) {
      console.error('Token acquisition failed:', err);
      return null;
    }
  }, [instance, accounts]);
  
  // Fetch schedules
  const loadSchedules = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE}/schedule-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load schedules');
      
      const data = await response.json();
      setSchedules(data.schedules || []);
    } catch (err) {
      console.error('Failed to load schedules:', err);
      setError(err.message);
    }
  }, [API_BASE, getToken]);
  
  // Fetch user profile
  const loadUserProfile = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      
      const response = await fetch(`${API_BASE}/user-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);
      } else {
        // Create default profile for new users
        setUserProfile({
          tier: 'trial',
          timezone: userTimezone,
          scrutinizeUsedThisMonth: 0
        });
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
      // Default to trial tier if profile load fails
      setUserProfile({ tier: 'trial', timezone: userTimezone });
    }
  }, [API_BASE, getToken, userTimezone]);
  
  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadSchedules(), loadUserProfile()]);
      setLoading(false);
    };
    init();
  }, [loadSchedules, loadUserProfile]);
  
  // Calculate scheduled runs per month for validation
  const calculateMonthlyRuns = (schedule) => {
    if (schedule.frequency === 'weekly') {
      const days = JSON.parse(schedule.weeklyDays || '[]');
      return days.length * 4; // ~4 weeks per month
    } else {
      const dates = JSON.parse(schedule.monthlyDates || '[]');
      return dates.length;
    }
  };
  
  // Calculate total monthly runs from all active schedules
  const getTotalMonthlyRuns = useCallback(() => {
    return schedules
      .filter(s => s.status === 'active')
      .reduce((total, s) => total + calculateMonthlyRuns(s), 0);
  }, [schedules]);
  
  // Validate if new schedule would exceed limits
    const validateScheduleLimits = () => {
    const tier = userProfile?.tier || 'trial';
    const limits = TIER_LIMITS[tier];

    // Safety check — unknown tier
    if (!limits) return null;

    // Enterprise (null = unlimited) — no limits
    if (limits.schedules === null) return null;

    // Check schedule count
    const activeSchedules = schedules.filter(s => s.status === 'active').length;

    if (!editingSchedule && activeSchedules >= limits.schedules) {
      return `You've reached your limit of ${limits.schedules} schedules on the ${tier} tier.`;
    }

    // Enterprise unlimited scans
    if (limits.scrutinizePerMonth === null) return null;

    // Calculate runs this schedule would add
    let newRuns = 0;
    if (formData.frequency === 'weekly') {
      newRuns = formData.weeklyDays.length * 4;
    } else {
      newRuns = formData.monthlyDates.length;
    }

    // If editing, subtract the old schedule's runs
    let currentRuns = getTotalMonthlyRuns();
    if (editingSchedule) {
      currentRuns -= calculateMonthlyRuns(editingSchedule);
    }

    const totalRuns = currentRuns + newRuns;
    const remaining = limits.scrutinizePerMonth - (userProfile?.scrutinizeUsedThisMonth || 0);

    if (totalRuns > remaining) {
      return `This schedule would use ${newRuns} scans/month, but you only have ${remaining} Deep Scans remaining this month.`;
    }

    return null;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    
    // Validate required fields
    if (!formData.target.trim()) {
      setFormError('Target domain or IP is required');
      return;
    }
    if (!formData.industry) {
      setFormError('Please select an industry for AI-customized analysis');
      return;
    }
    if (formData.frequency === 'weekly' && formData.weeklyDays.length === 0) {
      setFormError('Please select at least one day of the week');
      return;
    }
    if (formData.frequency === 'monthly' && formData.monthlyDates.length === 0) {
      setFormError('Please select at least one date');
      return;
    }
    if (!formData.recipients.trim()) {
      setFormError('At least one email recipient is required');
      return;
    }
    
    // Validate email addresses
    const emails = formData.recipients.split(',').map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      setFormError(`Invalid email address: ${invalidEmails[0]}`);
      return;
    }
    
    // Validate tier limits
    const limitError = validateScheduleLimits();
    if (limitError) {
      setFormError(limitError);
      return;
    }
    
    setSaving(true);
    
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      
      const payload = {
        ...formData,
        name: formData.name.trim() || formData.target.trim(),
        recipients: JSON.stringify(emails),
        weeklyDays: JSON.stringify(formData.weeklyDays),
        monthlyDates: JSON.stringify(formData.monthlyDates),
        timezone: userProfile?.timezone || userTimezone
      };
      
      const url = editingSchedule 
        ? `${API_BASE}/schedule-update/${editingSchedule.scheduleId}`
        : `${API_BASE}/schedule-create`;
        
      const method = editingSchedule ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save schedule');
      }
      
      // PRD-151 WP2: config change telemetry. `detail` is a fixed enum
      // ('create' | 'update') — never the schedule target/domain/recipients.
      try {
        trackTelemetry('action', {
          meta: { feature: 'config', action: 'schedule_save', detail: editingSchedule ? 'update' : 'create' },
        });
      } catch (e) { /* best-effort */ }

      // Refresh schedules list
      await loadSchedules();

      // Close modal and reset form
      setShowCreateModal(false);
      setEditingSchedule(null);
      resetForm();
      
    } catch (err) {
      console.error('Save schedule failed:', err);
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle schedule active/paused
  const handleToggleSchedule = async (schedule) => {
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      
      const response = await fetch(`${API_BASE}/schedule-toggle/${schedule.scheduleId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to toggle schedule');
      
      await loadSchedules();
    } catch (err) {
      console.error('Toggle schedule failed:', err);
      setError(err.message);
    }
  };
  
  // Delete schedule
  const handleDeleteSchedule = async (schedule) => {
    if (!window.confirm(`Are you sure you want to delete "${schedule.name}"?`)) {
      return;
    }
    
    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      
      const response = await fetch(`${API_BASE}/schedule-delete/${schedule.scheduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete schedule');
      
      await loadSchedules();
    } catch (err) {
      console.error('Delete schedule failed:', err);
      setError(err.message);
    }
  };
  
  // Edit schedule
  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name || '',
      target: schedule.target || '',
      industry: schedule.industry || '',
      frequency: schedule.frequency || 'weekly',
      weeklyDays: JSON.parse(schedule.weeklyDays || '[]'),
      monthlyDates: JSON.parse(schedule.monthlyDates || '[]'),
      hour: schedule.hour || 9,
      minute: schedule.minute || 0,
      recipients: JSON.parse(schedule.recipients || '[]').join(', '),
      reportFormat: schedule.reportFormat || 'link'
    });
    setShowCreateModal(true);
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      target: '',
      industry: '',
      frequency: 'weekly',
      weeklyDays: [],
      monthlyDates: [],
      hour: 9,
      minute: 0,
      recipients: '',
      reportFormat: 'link'
    });
    setFormError(null);
  };
  
  // Open create modal
  const openCreateModal = () => {
    setEditingSchedule(null);
    resetForm();
    setShowCreateModal(true);
  };
  
  // Toggle day selection
  const toggleDay = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      weeklyDays: prev.weeklyDays.includes(dayIndex)
        ? prev.weeklyDays.filter(d => d !== dayIndex)
        : [...prev.weeklyDays, dayIndex].sort((a, b) => a - b)
    }));
  };
  
  // Toggle date selection
  const toggleDate = (date) => {
    setFormData(prev => ({
      ...prev,
      monthlyDates: prev.monthlyDates.includes(date)
        ? prev.monthlyDates.filter(d => d !== date)
        : [...prev.monthlyDates, date].sort((a, b) => a - b)
    }));
  };
  
  // Format schedule frequency for display
  const formatFrequency = (schedule) => {
    if (schedule.frequency === 'weekly') {
      const days = JSON.parse(schedule.weeklyDays || '[]');
      const dayNames = days.map(d => DAY_NAMES[d]).join(', ');
      return `Weekly (${dayNames})`;
    } else {
      const dates = JSON.parse(schedule.monthlyDates || '[]');
      return `Monthly (${dates.join(', ')})`;
    }
  };
  
  // Check if user can create schedules
  const canCreateSchedule = () => {
    const tier = userProfile?.tier || 'trial';
    if (tier === 'free') return false;
    const limits = TIER_LIMITS[tier];
    if (!limits) return true;
    if (limits.schedules === null) return true; // unlimited (enterprise)
    const activeCount = schedules.filter(s => s.status === 'active').length;
    return activeCount < limits.schedules;
  };
  
  // Loading state
  if (loading) {
    return (
      <CleanModernLayout activePage="schedules">
      <div className="schedules-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">LOADING SCHEDULES...</p>
          <p className="loading-subtext">Retrieving your automated assessments</p>
        </div>
      </div>
      </CleanModernLayout>
    );
  }
  
  // Error state
  if (error) {
    return (
      <CleanModernLayout activePage="schedules">
      <div className="schedules-container">
        <div className="error-state">
          <h2 className="error-title">ERROR</h2>
          <p className="error-text">{error}</p>
          <button onClick={() => { setError(null); loadSchedules(); }} className="nav-button">
            Retry
          </button>
        </div>
      </div>
      </CleanModernLayout>
    );
  }
  
  // Upgrade prompt for users without scheduling access
  const tier = userProfile?.tier || 'trial';
  if (tier === 'free') {
    return (
      <CleanModernLayout activePage="schedules">
      <div className="schedules-container">
        <div className="schedules-branding">
          <h1 className="brand-title">DriftAlarm</h1>
          <p className="brand-subtitle">Automated Assessments</p>
        </div>

        <div className="upgrade-prompt">
          <div className="upgrade-icon">📅</div>
          <h2 className="upgrade-title">Automate Your Assessments</h2>
          <p className="upgrade-text">
            Automate your security assessments with industry-customized AI analysis
            delivered directly to your inbox.
          </p>
          <ul className="upgrade-features">
            <li>Weekly or monthly automated Deep Scans</li>
            <li>AI analysis customized to your industry</li>
            <li>Email reports to multiple recipients</li>
            <li>Track security posture over time</li>
          </ul>
          <div className="tier-comparison">
            <div className="tier-card standard">
              <h3>Standard</h3>
              <p className="tier-price">$99/month</p>
              <p className="tier-yearly">or $999/year</p>
              <ul>
                <li>3 schedules for assessments</li>
                <li>25 deep assessments per week</li>
                <li>Daily continuous monitoring</li>
              </ul>
              <button className="nav-button primary" onClick={() => navigate('/upgrade?tier=standard')}>
                Get Standard
              </button>
            </div>
            <div className="tier-card pro">
              <h3>Pro</h3>
              <p className="tier-price">$389/month</p>
              <p className="tier-yearly">or $3,899/year</p>
              <ul>
                <li>10 schedules</li>
                <li>Unlimited deep assessments</li>
                <li>Full EASM/CTEM platform</li>
              </ul>
              <button className="nav-button" onClick={() => navigate('/upgrade?tier=pro')}>
                Get Pro
              </button>
            </div>
          </div>
        </div>
      </div>
      </CleanModernLayout>
    );
  }
  
  // Main schedules view
  const activeSchedules = schedules.filter(s => s.status === 'active');
  const pausedSchedules = schedules.filter(s => s.status === 'paused');
  const limits = TIER_LIMITS[tier];

  // Helper to scroll to a schedule card
  const scrollToSchedule = (scheduleId) => {
    const element = document.getElementById(`schedule-${scheduleId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight-pulse');
      setTimeout(() => element.classList.remove('highlight-pulse'), 2000);
    }
  };

  return (
    <CleanModernLayout activePage="schedules">
    <div className="schedules-page">
      {/* Compact Header */}
      <div className="schedules-header">
        <h1 className="page-title">Scheduled Assessments</h1>
        <div className={`tier-badge-compact ${tier}`}>
          {tier.charAt(0).toUpperCase() + tier.slice(1)}
        </div>
      </div>

      <div className="schedules-layout">
        {/* Sidebar */}
        <aside className="schedules-sidebar">
          {/* Stats Circle */}
          <div className="sidebar-stats">
            <div className="stats-circle">
              <span className="stats-active">{activeSchedules.length}</span>
              <span className="stats-divider">/</span>
              <span className="stats-total">{limits?.schedules === null ? '\u221E' : (limits?.schedules ?? '?')}</span>
            </div>
            <span className="stats-label">Active Schedules</span>
          </div>

          {/* Timezone */}
          <div className="sidebar-timezone">
            <span className="timezone-label">Timezone</span>
            <span className="timezone-value">{userProfile?.timezone || userTimezone}</span>
          </div>

          {/* Active Section */}
          <div className="sidebar-section">
            <button
              className="section-header"
              onClick={() => setActiveSectionOpen(!activeSectionOpen)}
            >
              <span className="status-indicator active"></span>
              <span className="section-label">Active</span>
              <span className="section-count">{activeSchedules.length}</span>
              <span className={`section-arrow ${activeSectionOpen ? 'open' : ''}`}>▼</span>
            </button>
            {activeSectionOpen && (
              <div className="section-items">
                {activeSchedules.map(schedule => (
                  <button
                    key={schedule.scheduleId}
                    className="sidebar-item"
                    onClick={() => scrollToSchedule(schedule.scheduleId)}
                  >
                    <span className="item-target">{schedule.target}</span>
                  </button>
                ))}
                {activeSchedules.length === 0 && (
                  <span className="section-empty">No active schedules</span>
                )}
              </div>
            )}
          </div>

          {/* Paused Section */}
          <div className="sidebar-section">
            <button
              className="section-header"
              onClick={() => setPausedSectionOpen(!pausedSectionOpen)}
            >
              <span className="status-indicator paused"></span>
              <span className="section-label">Paused</span>
              <span className="section-count">{pausedSchedules.length}</span>
              <span className={`section-arrow ${pausedSectionOpen ? 'open' : ''}`}>▼</span>
            </button>
            {pausedSectionOpen && (
              <div className="section-items">
                {pausedSchedules.map(schedule => (
                  <button
                    key={schedule.scheduleId}
                    className="sidebar-item paused"
                    onClick={() => scrollToSchedule(schedule.scheduleId)}
                  >
                    <span className="item-target">{schedule.target}</span>
                  </button>
                ))}
                {pausedSchedules.length === 0 && (
                  <span className="section-empty">No paused schedules</span>
                )}
              </div>
            )}
          </div>

          {/* Create Button */}
          <div className="sidebar-create">
            {canCreateSchedule() ? (
              <button className="create-btn" onClick={openCreateModal}>
                <span className="create-icon">+</span>
                New Schedule
              </button>
            ) : (
              <div className="limit-notice">
                <span>Limit reached ({limits?.schedules ?? 0})</span>
                {tier === 'standard' && (
                  <button className="upgrade-btn" onClick={() => navigate('/upgrade?tier=pro')}>
                    Upgrade
                  </button>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="schedules-main">
          {schedules.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📅</div>
              <h3 className="empty-title">Schedule Automated Deep Scans</h3>
              <p className="empty-text">
                Set up recurring Deep Scans to continuously monitor your targets.
                Each scan delivers AI-powered security analysis customized to your
                industry, with findings emailed directly to your team.
              </p>
              <ul className="empty-features">
                <li>Comprehensive vulnerability and exposure assessment</li>
                <li>AI analysis tailored to your industry's threat landscape</li>
                <li>Automated email reports to multiple recipients</li>
                <li>Weekly or monthly scheduling with flexible timing</li>
              </ul>
              <button className="btn-primary" onClick={openCreateModal}>
                Create Your First Schedule
              </button>
            </div>
          ) : (
            <>
              {/* Main content header with create button */}
              <div className="main-content-header">
                <h2 className="main-content-title">
                  Scheduled Deep Scans
                  <span className="schedule-count">({schedules.length})</span>
                </h2>
                {canCreateSchedule() && (
                  <button className="btn-primary create-main-btn" onClick={openCreateModal}>
                    + New Schedule
                  </button>
                )}
              </div>

              {/* Active Schedules Section */}
              {activeSchedules.length > 0 && (
                <section className="main-section">
                  <h2 className="main-section-title">
                    <span className="status-dot active"></span>
                    Active Schedules
                  </h2>
                  <div className="cards-grid">
                    {activeSchedules.map(schedule => (
                      <div
                        key={schedule.scheduleId}
                        id={`schedule-${schedule.scheduleId}`}
                        className="schedule-card active"
                      >
                        <div className="card-header">
                          <span className="card-target">{schedule.target}</span>
                          <span className="status-badge active">ACTIVE</span>
                        </div>
                        <div className="card-body">
                          <div className="card-row">
                            <span className="row-label">Frequency</span>
                            <span className="row-value">{formatFrequency(schedule)}</span>
                          </div>
                          {schedule.nextRunAt && (
                            <div className="card-row highlight">
                              <span className="row-label">Next Run</span>
                              <span className="row-value">
                                {new Date(schedule.nextRunAt).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          )}
                          {schedule.lastRunAt && (
                            <div className="card-row">
                              <span className="row-label">Last Run</span>
                              <span className={`row-value ${schedule.lastRunStatus}`}>
                                {new Date(schedule.lastRunAt).toLocaleDateString()} • {schedule.lastRunStatus}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="card-actions">
                          {schedule.lastRunScanId && (
                            <button
                              className="action-btn view"
                              onClick={() => navigate(`/scrutinize-result/${schedule.lastRunScanId}`)}
                            >
                              Report
                            </button>
                          )}
                          <button
                            className="action-btn edit"
                            onClick={() => handleEditSchedule(schedule)}
                          >
                            Edit
                          </button>
                          <button
                            className="action-btn pause"
                            onClick={() => handleToggleSchedule(schedule)}
                          >
                            Pause
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteSchedule(schedule)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Paused Schedules Section */}
              {pausedSchedules.length > 0 && (
                <section className="main-section paused">
                  <h2 className="main-section-title">
                    <span className="status-dot paused"></span>
                    Paused Schedules
                  </h2>
                  <div className="cards-grid">
                    {pausedSchedules.map(schedule => (
                      <div
                        key={schedule.scheduleId}
                        id={`schedule-${schedule.scheduleId}`}
                        className="schedule-card paused"
                      >
                        <div className="card-header">
                          <span className="card-target">{schedule.target}</span>
                          <span className="status-badge paused">PAUSED</span>
                        </div>
                        <div className="card-body">
                          <div className="card-row">
                            <span className="row-label">Frequency</span>
                            <span className="row-value">{formatFrequency(schedule)}</span>
                          </div>
                        </div>
                        <div className="card-actions">
                          <button
                            className="action-btn resume"
                            onClick={() => handleToggleSchedule(schedule)}
                          >
                            Resume
                          </button>
                          <button
                            className="action-btn edit"
                            onClick={() => handleEditSchedule(schedule)}
                          >
                            Edit
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteSchedule(schedule)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
      </div>
      
      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {/* Sticky Header */}
            <div className="modal-header">
              <h2>{editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            {/* Scrollable Body */}
            <div className="modal-body">
              <form id="schedule-form" onSubmit={handleSubmit}>
                {formError && (
                  <div className="form-error">
                    <span className="error-icon">⚠️</span>
                    {formError}
                  </div>
                )}

                {/* Target Configuration Section */}
                <div className="form-section">
                  <h3 className="section-title-form">Target Configuration</h3>

                  <div className="form-group">
                    <label>Target Domain or IP <span className="required">*</span></label>
                    <input
                      type="text"
                      value={formData.target}
                      onChange={e => setFormData({...formData, target: e.target.value})}
                      placeholder="e.g., example.com or 192.168.1.1"
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Schedule Name (optional)</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Weekly Security Check"
                      className="form-input"
                    />
                    <span className="form-hint">Leave blank to use target domain as name</span>
                  </div>

                  <div className="form-group">
                    <label>Target Industry <span className="required">*</span></label>
                    <select
                      value={formData.industry}
                      onChange={e => setFormData({...formData, industry: e.target.value})}
                      className="form-select"
                      required
                    >
                      <option value="">Select industry...</option>
                      {INDUSTRIES.map(ind => (
                        <option key={ind.code} value={ind.code}>{ind.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Schedule Timing Section */}
                <div className="form-section">
                  <h3 className="section-title-form">Schedule Timing</h3>

                  <div className="form-group">
                    <label>Frequency <span className="required">*</span></label>
                    <div className="frequency-toggle">
                      <button
                        type="button"
                        className={`freq-btn ${formData.frequency === 'weekly' ? 'active' : ''}`}
                        onClick={() => setFormData({...formData, frequency: 'weekly', monthlyDates: []})}
                      >
                        Weekly
                      </button>
                      <button
                        type="button"
                        className={`freq-btn ${formData.frequency === 'monthly' ? 'active' : ''}`}
                        onClick={() => setFormData({...formData, frequency: 'monthly', weeklyDays: []})}
                      >
                        Monthly
                      </button>
                    </div>
                  </div>

                  {/* Weekly Days Selector */}
                  {formData.frequency === 'weekly' && (
                    <div className="form-group">
                      <label>Days of Week <span className="required">*</span></label>
                      <div className="days-selector">
                        {DAY_NAMES_FULL.map((day, index) => (
                          <button
                            key={index}
                            type="button"
                            className={`day-btn ${formData.weeklyDays.includes(index) ? 'selected' : ''}`}
                            onClick={() => toggleDay(index)}
                          >
                            {DAY_NAMES[index]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Monthly Dates Selector */}
                  {formData.frequency === 'monthly' && (
                    <div className="form-group">
                      <label>Dates of Month <span className="required">*</span></label>
                      <div className="dates-selector">
                        {[...Array(31)].map((_, i) => (
                          <button
                            key={i + 1}
                            type="button"
                            className={`date-btn ${formData.monthlyDates.includes(i + 1) ? 'selected' : ''}`}
                            onClick={() => toggleDate(i + 1)}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <span className="form-hint">Scans skip if date doesn't exist in month</span>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Time ({userProfile?.timezone || userTimezone})</label>
                    <div className="time-selector">
                      <select
                        value={formData.hour}
                        onChange={e => setFormData({...formData, hour: parseInt(e.target.value)})}
                        className="form-select time-select"
                      >
                        {[...Array(24)].map((_, h) => (
                          <option key={h} value={h}>
                            {h === 0 ? '12' : h > 12 ? h - 12 : h} {h < 12 ? 'AM' : 'PM'}
                          </option>
                        ))}
                      </select>
                      <span className="time-separator">:</span>
                      <select
                        value={formData.minute}
                        onChange={e => setFormData({...formData, minute: parseInt(e.target.value)})}
                        className="form-select time-select"
                      >
                        {[0, 15, 30, 45].map(m => (
                          <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Advanced Settings - Collapsible */}
                <div className={`form-section collapsible ${advancedOpen ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="section-toggle"
                    onClick={() => setAdvancedOpen(!advancedOpen)}
                  >
                    <span className="toggle-arrow">{advancedOpen ? '▼' : '▶'}</span>
                    <span>Advanced Settings</span>
                  </button>

                  {advancedOpen && (
                    <div className="collapsible-content">
                      <div className="form-group">
                        <label>Email Recipients <span className="required">*</span></label>
                        <textarea
                          value={formData.recipients}
                          onChange={e => setFormData({...formData, recipients: e.target.value})}
                          placeholder="security@company.com, ciso@company.com"
                          className="form-textarea"
                          rows={2}
                          required
                        />
                        <span className="form-hint">Separate multiple addresses with commas</span>
                      </div>

                      <div className="form-group">
                        <label>Report Format</label>
                        <div className="format-options">
                          <label className="format-option">
                            <input
                              type="radio"
                              name="reportFormat"
                              value="link"
                              checked={formData.reportFormat === 'link'}
                              onChange={e => setFormData({...formData, reportFormat: e.target.value})}
                            />
                            <span>Email with link</span>
                          </label>
                          <label className={`format-option ${!['pro', 'enterprise'].includes(tier) ? 'disabled' : ''}`}>
                            <input
                              type="radio"
                              name="reportFormat"
                              value="both"
                              checked={formData.reportFormat === 'both'}
                              onChange={e => setFormData({...formData, reportFormat: e.target.value})}
                              disabled={!['pro', 'enterprise'].includes(tier)}
                            />
                            <span>Link + PDF</span>
                            {!['pro', 'enterprise'].includes(tier) && <span className="pro-badge">PRO</span>}
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Usage Preview */}
                <div className="usage-preview">
                  <div className="usage-stats">
                    <div className="usage-stat">
                      <span className="usage-number">
                        {formData.frequency === 'weekly'
                          ? formData.weeklyDays.length * 4
                          : formData.monthlyDates.length}
                      </span>
                      <span className="usage-label">Scans/Month</span>
                    </div>
                    <div className="usage-stat">
                      <span className="usage-number">
                        {limits?.scrutinizePerMonth === null ? '\u221E' : ((limits?.scrutinizePerMonth ?? 0) - (userProfile?.scrutinizeUsedThisMonth || 0))}
                      </span>
                      <span className="usage-label">Remaining</span>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Sticky Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowCreateModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="schedule-form"
                className="btn-submit"
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingSchedule ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </CleanModernLayout>
  );
}

export default Schedules;
